import { computed, ref } from 'vue'
import string from '@lib/string'
import clsDialogModel from '@cls/clsDialogModel'
import {vat as vatlist, unit as unitlist} from '@app/list';
import { workorders as api } from '@/app/api'
import Constants from '@app/consts'
import {date, time} from '@lib/date'
import {numeric} from '@lib/numeric'

    const _typeProductLine = "product";
    const _typeInvoiceLine = "invoice_product";
    const _typeTextLine    = "text";
    const _typeHourLine    = "invoice_hour";


    // ihp_type - internally ONLY, we use invoice_hour, which is actually just an invoice line. 
    // We use it to differentiate between product and hour lines to see a separate margin.
    
    let seqId = 0; 
    function getNewLineId() {
        return ++seqId;
    }

    class clsInvoiceLine {
        // Internally, we use the same field names as used in invoices. 
        // The lineId is a field for internal use only. We use it so that the reactive frontend can use it 
        // for identification. 
        // It is not related to the id of the invoice line in the invoice or any other id.
        // 
        checked            = false;
        lineId             = getNewLineId();
        ihp_type           = null; // 'invoice_product', 'invoice_hour', 'text'
        ihp_amount         = null; 
        id_unity           = null; 
        ihp_pd_name        = null; 
        ihp_purchase_price = null; 
        ihp_sales_price    = null;
        id_product         = null;
        id_vat             = null;

        constructor(data) {        
            data = data || {};
        

            this.ihp_amount = data.ihp_amount || data.quantity || data.amount || 1;
            this.id_unity = data.id_unity || data.id_unit || null;
            this.ihp_pd_name = data.ihp_pd_name || data.pd_name || null;
            this.ihp_purchase_price = data.ihp_purchase_price || data.purchase_price_excl_vat || data.pd_purchase_price || 0;
            this.ihp_sales_price = data.ihp_sales_price || data.sales_price_excl_vat || data.pd_sales_price_excl_vat || 0;
            this.id_product = data.id_product || null;
            this.id_vat = data.id_vat || Constants.defaults.id_vat;
            this.pd_name = data.pd_name || null;
            this.pd_code = data.pd_code || null;

            this.ihp_type = data.ihp_type || _typeInvoiceLine;
            if (!!data.id_product) {
                this.ihp_type = _typeProductLine;
            }   // Default to invoice_product for now, as we only create those via the dialog.

        }   

        get isTextLine() {
            return string.compare(this.ihp_type, _typeTextLine);
        }
        get isHourLine() {
            return string.compare(this.ihp_type, _typeHourLine);
        }
        get isProductLine() {
            return string.compare(this.ihp_type, _typeProductLine);
        }
        get isInvoiceLine() {
            return string.compare(this.ihp_type, _typeInvoiceLine);
        }

        get canCalculateMargin() {
            if (this.isTextLine) {
                return false;
            }
            if (!this.ihp_purchase_price) {
                return false;
            }
            if (!this.ihp_sales_price) {
                return false;
            }
            return true;
        }
        get marginTotal() {
            var cost = this.total_amount_purchase || 0;
            var sales = this.total_amount_sales || 0;

            return numeric.round(sales - cost, 2);
        }
        get marginPct() {
            if (!this.total_amount_purchase) {
                return 100;
            }
            return numeric.round(100 * (this.marginTotal||0)/this.total_amount_purchase, 0);
        }

        get total_amount_sales() { 
            var pctFactor = 1; // By default, no percentage.
            if (this.id_unity) {
                let unity = unitlist.one(this.id_unity);            
                if (!!unity.is_percentage) {
                    pctFactor = 0.01;
                }
            }
            let price = this.ihp_sales_price;
            return numeric.round(pctFactor * this.ihp_amount * price,2);
        }
        get total_amount_purchase() { 
            var pctFactor = 1; // By default, no percentage.
            if (this.id_unity) {
                let unity = unitlist.one(this.id_unity);            
                if (!!unity.is_percentage) {
                    pctFactor = 0.01;
                }
            }
            let price = this.ihp_purchase_price;
            return numeric.round(pctFactor * this.ihp_amount * price,2);
        }

        // toJSON returns the data as it is used in invoices.
        toJSON() {
            return {
                ihp_amount:         this.ihp_amount,
                id_unity:           this.id_unity,
                ihp_pd_name:        this.ihp_pd_name,
                ihp_purchase_price: this.ihp_purchase_price,
                ihp_sales_price:    this.ihp_sales_price,
                id_product:         this.id_product,
                id_employee:        this.id_employee,
                id_vat:             this.id_vat,
                pd_name:            this.pd_name,
                pd_code:            this.pd_code,
                ihp_type:           this.ihp_type == _typeHourLine ? _typeInvoiceLine : this.ihp_type, // When saving, we convert invoice_hour to invoice_product, as we only use the former internally in the dialog.;
            }
        }
    }

    class clsHourLine extends clsInvoiceLine {
        id_employee = null;

        constructor(data) {        
            data = data || {};
            super(data); 
            var dt = date.fmt.local(data.date);
            var toFromTime = "";
            var type = "";
            if (data.start_time || data.end_time) {
                var tFrom = time.minutes2hourminutes(data.start_time);
                var tTo = time.minutes2hourminutes(data.end_time);
                toFromTime = ` | ${tFrom} - ${tTo}`;
            }
            if (data.hour_type) {
                type = ` | ${data.hour_type}`;
            }

            this.ihp_type    = _typeHourLine;
            this.id_employee = data.id_employee || null;
            this.quantity    = Number(time.fmtMinutes(data.total_minutes||0, '%h.dd%'));
            this.id_unity    = unitlist.getHourType()?.id  || null;
            this.ihp_pd_name = `Uren ${data.employee} ${dt}${toFromTime}${type}`;
            this.id_product  = null;
            this.id_vat      = Constants.defaults.id_vat;
        }   
    }

    class clsTextLine extends clsInvoiceLine {

        constructor(data) {        
            data = data || {};
            super({}); // No properties to take over.

            this.ihp_type    = _typeTextLine;
            this.ihp_pd_name = data.ihp_pd_name || data.text || '';
        }   
    }


    /**
     */
    class clsDialogCreateInvoice extends clsDialogModel {
        lines = [];
        id_workorder = null;
        description = null; 
        appointments = [];

        get fillable() { return ['xlines', 'xid_workorder']; }

        get isTextLine()          { return string.compare(this.ihp_type, _typeTextLine); }
        get isHourLine()          { return string.compare(this.ihp_type, _typeHourLine); }
        get isProductLine()       { return string.compare(this.ihp_type, _typeProductLine); }
        get isInvoiceLine()       { return string.compare(this.ihp_type, _typeInvoiceLine); }
        get textLines()           { return this.lines.filter(line => line.isTextLine);    }
        get hourLines()           { return this.lines.filter(line => line.isHourLine);    }
        get productLines()        { return this.lines.filter(line => line.isProductLine); }
        get invoiceLines()        { return this.lines.filter(line => line.isInvoiceLine); } // aka other lines.
        get checkedLines()        { return this.lines.filter( line => line.checked); } 
        get checkedHourLines()    { return this.hourLines.filter( line => line.checked); } 
        get checkedTextLines()    { return this.textLines.filter( line => line.checked); } 
        get checkedProductLines() { return this.productLines.filter( line => line.checked); } 
        get checkedInvoiceLines() { return this.invoiceLines.filter( line => line.checked); } 
        get checkedCount()        { return this.checkedLines.length; }

        // Show totals and margin overview grouped per line type 
        showTotalSummary = false;
        
        // gAllChecked is the readonly getter which is just used for displaying the value in the checkbox in the header. 
        // We do NOT use it for setting the value as vue would both listen to the same state while setting and getting, which 
        // does not work well. 
        get roAllChecked() { return this.allChecked; }
        set roAllChecked(value) { /* DO NOTHING on purpose. */ }
        toggleChecked() {
            var bAllChecked = !this.lines.find( line => !line.checked);
            this.lines.forEach( line => line.checked = !bAllChecked);
        }

        // load does just fill the model with default values. No server interaction.
        async load({id}) {
            return this.setLoadingWhile( async () => {
                var result = await api.loadCreateInvoiceData(id);
                return result.data || {};
            });
        }

        // commit in our case just returns the data to the caller. No server interaction.
        // data already contains the toJSON()'d data members
        async save(data) {
            return data;
        }    

        toProductLine(product) {
            if (!product.id_product && product.id) {
                // When imported from selectlist dialog, we only get a product is, not a id_product.
                product.id_product = product.id;
            }
            var line = new clsInvoiceLine(product);
            return line;
        }
        toHourLine(hour) {
            var line = new clsHourLine(hour);
            return line;
        }
        toTextLine(textLine) {
            var line = new clsTextLine(textLine);
            return line;
        }

        // Fill the model with data. 
        // Convert hours and products to lines for easier handling in the dialog.
        fill({id, hours=[], products=[], description=null, appointments=[]} = {}) {
            this.id = id;
            var lines = [];
            (hours||[]).forEach( (hour) => {
                lines.push(this.toHourLine(hour));
            });
            (products||[]).forEach( (prod) => {
                lines.push(this.toProductLine(prod));
            });
            this.description = description; 
            this.appointments = appointments;

            this.lines = lines;
        }

        get marginSummary() {
            var purchaseHours = this.lines.filter(line => line.isHourLine).reduce( (sum, line) => sum + (line.total_amount_purchase || 0), 0);
            var salesHours = this.lines.filter(line => line.isHourLine).reduce( (sum, line) => sum + (line.total_amount_sales || 0), 0);
            var marginHours = numeric.round(salesHours - purchaseHours, 2);
            var canMarginPctHours = !this.lines.find( line => line.isHourLine && !line.canCalculateMargin);
            var marginHoursPct = 0; 
            if (canMarginPctHours && purchaseHours) {
                marginHoursPct = numeric.round(100 * marginHours / purchaseHours, 0);
            } 
        

            var purchaseMaterial = this.lines.filter(line => line.isProductLine).reduce( (sum, line) => sum + (line.total_amount_purchase || 0), 0);
            var salesMaterial = this.lines.filter(line => line.isProductLine).reduce( (sum, line) => sum + (line.total_amount_sales || 0), 0);
            var marginMaterial = numeric.round(salesMaterial - purchaseMaterial, 2);
            var canMarginPctMaterial = !this.lines.find( line => line.isProductLine && !line.canCalculateMargin);
            var marginMaterialPct = 0; 
            if (canMarginPctMaterial && purchaseMaterial) {
                marginMaterialPct = numeric.round(100 * marginMaterial / purchaseMaterial, 0);
            } 

            var purchaseOther = this.lines.filter(line => line.isInvoiceLine).reduce( (sum, line) => sum + (line.total_amount_purchase || 0), 0);
            var salesOther = this.lines.filter(line => line.isInvoiceLine).reduce( (sum, line) => sum + (line.total_amount_sales || 0), 0);
            var marginOther = numeric.round(salesOther - purchaseOther, 2);
            var canMarginPctOther = !this.lines.find( line => line.isInvoiceLine && !line.canCalculateMargin);
            var marginOtherPct = 0; 
            if (canMarginPctOther && purchaseOther) {
                marginOtherPct = numeric.round(100 * marginOther / purchaseOther, 0);
            } 

            return {
                hours:    {purchase: purchaseHours,    sales: salesHours,    margin: marginHours,    canMarginPct: canMarginPctHours,    marginPct: marginHoursPct},
                material: {purchase: purchaseMaterial, sales: salesMaterial, margin: marginMaterial, canMarginPct: canMarginPctMaterial, marginPct: marginMaterialPct},
                other:    {purchase: purchaseOther,    sales: salesOther,    margin: marginOther,    canMarginPct: canMarginPctOther,    marginPct: marginOtherPct},
            }
        }
        get totals() {
            var purchase = this.lines.reduce( (sum, line) => sum + (line.total_amount_purchase || 0), 0);
            var sales = this.lines.reduce( (sum, line) => sum + (line.total_amount_sales || 0), 0);
            var margin = numeric.round(sales - purchase, 2);
            return {
                purchase: purchase,
                sales: sales,
                margin: margin,
                marginPct: (!purchase)? 0 : numeric.round(100 * margin / purchase, 0),
                canMarginPct: this.canCalculateMargin
            }
        }

        get canCalculateMargin() {
            return !this.lines.find( line => !line.isTextLine && !line.canCalculateMargin);
        }

        get marginPct() {
            if (!this.total_amount_purchase) {
                return 100;
            }
            return numeric.round(100 * (this.marginTotal||0)/this.total_amount_purchase, 0);
        }

        // Reload the data
        async reload() {    
            await this.loadAndFill({id: this.id});
        }

        removeLine(line) {
            this.lines = this.lines.filter( (l) => line != l); 
        }

        removeCheckedLines() {
            this.lines = this.lines.filter( (l) => !l.checked); 
        }
        // Combine the checked lines.
        // - 1. Less than 2 lines checked: 
        //      Do nothing.
        //
        // - 2. One or more of the selected lines are text lines: 
        //      Alert forbidden
        //
        // - Combine 2 hour lines: 
        //
        // - Combine 2 product lines: 
        // - Combine 1 hour line with 1 product - or other line: 
        // - Combine 1 other line with any line: 
        //   forbidden. 
        combineCheckedLines() {
            // Make sure at least 2 lines are checked.
            if (this.checkedCount < 2) {
                return;
            }
            // 1. 
            if (this.checkedTextLines.length) {
                return noty.alert("Het is niet toegestaan om tekstregels samen te voegen met andere regels.");
            }
            // Combining any type with each other results in a 'other' line. It is a normal invoice line without a product.
            var nTypes = 0; 
            nTypes += (this.checkedHourLines.length) ? 1 : 0;
            nTypes += (this.checkedProductLines.length) ? 1 : 0;
            nTypes += (this.checkedInvoiceLines.length) ? 1 : 0;

            var purchasePrice = this.checkedLines.reduce( (sum, line) => sum + (line.total_amount_purchase || 0)  , 0);
            var salesPrice    = this.checkedLines.reduce( (sum, line) => sum + (line.total_amount_sales || 0)  , 0);
            var description   = this.checkedLines.reduce( (descr, line) => (line.ihp_pd_name ? `${descr}${descr?'<br>':''}${line.ihp_pd_name}` : descr), '');

            var line = this.toProductLine({
                    ihp_amount: 1, // We set the amount to 1, as the total price is already in the purchase and sales price. 
                    ihp_pd_name: description,
                    ihp_purchase_price: purchasePrice,
                    ihp_sales_price: salesPrice,
            })
            this.removeCheckedLines();
            this.lines.push(line);

        }

        addTextLine(textLine) {
            var line = this.toTextLine(textLine)
            this.lines.push(line);
        }

        addProductLine(productLine) {
            var line = this.toProductLine(productLine||{})
            this.lines.push(line);
        }

        toJSON() {
            var data = {
                id_workorder: this.id_workorder,
                lines: this.lines.map( line => line.toJSON()),
            }
            return data;
        }

        /**
         * model is the toJSON representation of this object.
         * We just return the model. Saving the invoice is not ours. The caller has to take care of this.
         * 
         * @param model 
         */
        async save(model) {
            model.id_workorder = this.id;
            return model; 
//            return this.setLoadingWhile( async () => {
//                var result = await api.createInvoice(model);
//                return result.data || {};
//            });
        }
    }


    let obj = null;

    function useDialogModelCreateInvoice({form=null} = {}) {
        if (!obj) {
            obj = ref (new clsDialogCreateInvoice( { 
                modelName: "workorder_createinvoice",         
                title: "Factuurvoorstel",
                forms: form, 
                icon: 'invoice', 
                saveText: 'Maak factuur',
                noSubTitle: true, 
                useDirtyCheck: true,
                keepOpen: true, 
                setKeyOnOpen: true
            }));
        }
        return obj;
    }
 export default useDialogModelCreateInvoice;
